import { BaseTicketController } from '../../../../common/base/ticketController';
import _ from 'lodash';
import { ManualDataTypesByTenant } from './upload.constants';

export class UploadController extends BaseTicketController {
  constructor(
    controllerService,
    ticketService,
    fdFormFieldsService,
    $q,
    TicketStates,
    DataTypeField,
    uiModalDeclarationService,
    ZiraatBankUploadTemplateUrl,
    UploadFields,
    $scope,
  ) {
    'ngInject';
    super({
      controllerService,
      ticketService,
      fdFormFieldsService,
      $q,
      TicketStates,
      DataTypeField,
      ZiraatBankUploadTemplateUrl,
      UploadFields,
      uiModalDeclarationService,
      $scope,
    });

    // Start with empty data model
    this.mainForms = {};
    this.manualDataTypes = [];
    this.inputForms = {};
    this.data = {};
    this.accordionMeta = {};

    const tenant = this.envService.getTenant();
    const ManualDataTypes = ManualDataTypesByTenant(tenant);

    // Setup manual data types
    _.forEach(ManualDataTypes, (dataType, i) => {
      this.manualDataTypes.push(dataType);
      this.inputForms[dataType] = [];
      this.data[dataType] = [];
      this.accordionMeta[dataType] = {
        isOpen: i === 0,
      };
    });

    this.uploadFields = this.setupUploadFields(UploadFields);
  }

  $onInit() {
    super.$onInit();

    // Handle custom santander behaviour for this view
    if (this.envService.isTenant(this.custom.santander)) {
      // Since santander uploads their data as oldbank
      // in a background prozess, they dont need this view.
      // Therefore we display a waiting screen and switch
      // to the new view as soon as the data has been processed
      // or this step should manually be skipped.
      const viewStatusOptions = {
        reason: this.ticket.status,
        status: 'pending',
        icon: 'fa-coffee',
        cta: () => this.skip(),
        ctaTitle: 'app.tickets.upload.skip.cta.title',
      };
      this.ticketService.viewStatusChanged(this.ticket.id, viewStatusOptions);
    }

    // Show mask for standing orders for commerzbank by default
    if (this.envService.isTenant(this.custom.commerzbank)) {
      this.activeTab = 1;
    }

    this.uploadType = _.get(this.appConfig, 'transferringUpload');

    if (this.uploadType === 'AGREE21_FORMER_BANK21') {
      // remove the manual section
      this.manualDataTypes = [];

      // add the custom section
      this.uploadFields[0].label = 'app.ticket.upload.balanceListAgree21';
      this.uploadFields.push({
        label: 'app.ticket.upload.balanceListBank21',
        upload: 'uploadCustomBalanceList',
      });

      this.uploadBalanceListsBundled = true;
    } else if (this.uploadType === 'AGREE21_FORMER_SDVBASE') {
      this.manualDataTypes = [];

      this.uploadFields.push({
        label: 'app.ticket.upload.custom',
        upload: 'uploadCustomBalanceList',
      });

      this.uploadBalanceListsBundled = true;
    } else if (this.uploadType === 'SDVBASE_XLSX') {
      this.uploadFields[0].label = 'app.ticket.upload.receivedPayments';
      this.uploadFields[0].upload = 'uploadReceivedPayments';

      this.uploadFields.push({
        label: 'app.ticket.upload.directDebits',
        upload: 'uploadSepaDirectDebits',
      });

      this.uploadFields.push({
        label: 'app.ticket.upload.standingOrders',
        upload: 'uploadStandingOrders',
      });
    }

    if (this.ticket.oldIbanDeprecated) {
      // Show hint for oldIbanDeprecated flag
      this.modal = this.uiModalDeclarationService.$uibModal.open({
        component: 'userImportComponent',
        size: 'lg',
      });
    }

    // this holds all files to upload on submit.
    // angular will create the keys when rendering the interface, as the upload fields are bound to this
    this.uploadFiles = [];
  }

  get isZiraatBank() {
    return _.get(this.appConfig, 'transferringUpload') === 'ZIRAAT';
  }

  get templateUrl() {
    return this.ZiraatBankUploadTemplateUrl;
  }

  setupUploadFields(UploadFields) {
    const tenant = this.envService.getTenant();
    const uploadConfig = this.config.ui.transferring.upload;

    // Filter for enabled upload fields
    return _.filter(UploadFields, (field, type) => {
      // NEW: Use custom config for field configuration
      if (angular.isDefined(uploadConfig[type])) {
        return uploadConfig[type];
      }

      // OLD: Use custom field setup

      // Handle enabled flag
      if (angular.isDefined(field.enabled)) {
        // Handle boolean
        if (_.isBoolean(field.enabled)) {
          return field.enabled;
        }
        // Handle array with tenant names (whitelist)
        if (angular.isArray(field.enabled)) {
          return _.includes(field.enabled, tenant);
        }
      }

      // Handle disabled flag
      if (angular.isDefined(field.disabled)) {
        // Handle boolean
        if (_.isBoolean(field.disabled)) {
          return !field.disabled;
        }
        // Handle array with tenant names (blacklist)
        if (angular.isArray(field.disabled)) {
          return !_.includes(field.disabled, tenant);
        }
      }

      return false;
    });
  }

  uploadReceivedPayments(file) {
    return this.ticketService.uploadReceivedPayments(this.ticket.id, file);
  }

  uploadSepaDirectDebits(file) {
    return this.ticketService.uploadSepaDirectDebits(this.ticket.id, file);
  }

  uploadStandingOrders(file) {
    return this.ticketService.uploadStandingOrders(this.ticket.id, file);
  }

  uploadAllTransactionsParallelZiraat(partners, standingOrders, transactions) {
    return this.ticketService.uploadAllTransactionsParallelZiraat(
      this.ticket.id,
      partners,
      standingOrders,
      transactions,
    );
  }

  uploadScheduledTransaction(file) {
    return this.ticketService.uploadScheduledTransaction(this.ticket.id, file);
  }

  uploadCustomBalanceList(commonBalanceFile, customBalanceFile) {
    /*
      this.pendingUpload and this.dataUploaded is only used for kwh-light with
      the enabled config 'this.config.ui.transferring.upload.skipConfirmDialog'.
      Add this to others if necessary.
      */
    this.pendingUpload = true;

    return this.ticketService
      .uploadBalanceList(this.ticket.id, commonBalanceFile, customBalanceFile)
      .then((result) => {
        this.pendingUpload = false;
        this.dataUploaded = true;
      });
  }

  uploadBalanceList(file) {
    /*
      this.pendingUpload and this.dataUploaded is only used for kwh-light with
      the enabled config 'this.config.ui.transferring.upload.skipConfirmDialog'.
      Add this to others if necessary.
      */
    this.pendingUpload = true;

    return this.ticketService
      .uploadBalanceList(this.ticket.id, file)
      .then((result) => {
        this.pendingUpload = false;
        this.dataUploaded = true;
      });
  }

  remove(dataModel, key) {
    _.remove(this.data[key], dataModel);
  }

  add($event, key) {
    if ($event && this.accordionMeta[key]) {
      $event.stopPropagation();
      $event.preventDefault();
    }
    // Ensure already added form(s) are valid
    this.setSubmitted(key);
    this.fdFormFieldsService.validate(this.mainForms[key]).then(() => {
      const dataBody = this.createDataBody(key);
      this.data[key].push(dataBody);
    });
  }

  createDataBody(type) {
    const { DataTypeField } = this;
    switch (type) {
      case DataTypeField.standingOrders: {
        return {
          currency: 'EUR',
        };
      }
    }
    return {};
  }

  submitEnabled() {
    for (let key in this.uploadFiles) {
      if (this.uploadFiles[key].length <= 0) {
        return false;
      }
    }
    return true;
  }

  submitFormZiraatBank() {
    const { partnersSPDIL, standingOrdersPAYSI, transactionsCHARE } =
      this.uploadFiles;

    this.isSubmitBusy = true;

    this.uploadAllTransactionsParallelZiraat(
      partnersSPDIL,
      standingOrdersPAYSI,
      transactionsCHARE,
    )
      .then((res) => {
        return this.ticketService
          .outgoingDataComplete(this.ticket.id)
          .busy(this, 'isSubmitBusy')
          .catch(() => {
            this.isSubmitBusy = false;
          });
      })
      .catch(() => {
        this.isSubmitBusy = false;
      });
  }

  submitForm() {
    const sendFilesInQueue = () => {
      // gather all files we want to upload and trigger upload for every file.
      const promiseStack = [];
      let commonBalanceListUploadFunc =
        this.UploadFields[this.DataTypeField.balanceList].upload;

      for (let uploadFunction in this.uploadFiles) {
        /* custom upload hack for sparda #1
          if custom upload is enabled, skip common balance list upload. The common balance list file will be uploaded with the custom upload function.
         */
        if (
          this.uploadBalanceListsBundled &&
          uploadFunction === commonBalanceListUploadFunc
        ) {
          continue;
        }

        let file = this.uploadFiles[uploadFunction][0];

        /* custom upload hack for sparda #2.
          we skipped common balance list upload and upload now common and custom balance list files with our custom upload, yay!
         */
        if (
          this.uploadBalanceListsBundled &&
          uploadFunction === this.UploadFields[this.DataTypeField.custom].upload
        ) {
          const commonBalanceList =
            this.uploadFiles[commonBalanceListUploadFunc][0] || undefined;
          promiseStack.push(this[uploadFunction](commonBalanceList, file));
          continue;
        }

        if (file) {
          // call specific upload function
          promiseStack.push(this[uploadFunction](file));
        }
      }

      if (promiseStack.length <= 0) {
        // return this.$q.reject('no files added');
        return this.$q.resolve('Resolve without uploads.');
      }

      return Promise.all(promiseStack);
    };

    // NOTE: For the sendManual call we cannot use the fancy `.busy()`
    // pattern since inside this method a $q.reject can happen and will
    // be returned. Therefore a TypeError will occure because
    // "busy is not a function" in the $q context but for $http.
    this.isSubmitBusy = true;
    sendFilesInQueue()
      .then(() => {
        return this.sendManual();
      })
      .then(() => {
        if (
          this.config.ui.transferring.upload.skipConfirmDialog &&
          (this.pendingUpload || !this.dataUploaded)
        ) {
          return this.$q.reject();
        }

        return this.ticketService
          .outgoingDataComplete(this.ticket.id)
          .busy(this, 'isSubmitBusy');
      })
      .catch(() => (this.isSubmitBusy = false));
  }

  submit() {
    if (this.isZiraatBank) {
      this.submitFormZiraatBank();
      return;
    }

    const { $scope } = this;
    if (
      this.submitEnabled() ||
      this.config.ui.transferring.upload.skipConfirmDialog
    ) {
      // All data is imported, skip confirmation
      this.submitForm();
      return;
    }

    $scope.confirmTitle = 'app.ticket.finances.form.confirmation.title';
    $scope.confirmBody = 'app.ticket.finances.form.confirmation.body';
    $scope.confirmDismiss = 'app.ticket.finances.form.confirmation.dismiss';
    $scope.confirmOk = 'app.ticket.finances.form.confirmation.confirm';

    this.uiModalDeclarationService.openUrl({
      url: 'app/common/ui/needsConfirmation/confirmation.tpl.html',
      options: {
        scope: $scope,
        onSuccess: this.submitForm.bind(this),
        modalClass: 'confirmation-modal',
      },
    });
  }

  sendManual() {
    if (!this.manualDataValid()) {
      _.forEach(this.accordionMeta, (val, key) => {
        // Open invalid sections
        this.accordionMeta[key].isOpen = this.mainForms[key].$invalid;
      });
      this.activeTab = 1;
      return this.$q.reject({ message: 'manual data invalid' });
    }

    // Send all manual data
    // NOTE: Actual we only send the standing orders

    if (!this.data.standingOrders || !this.data.standingOrders.length) {
      return this.$q.resolve();
    }

    return this.ticketService.postStandingOrders(
      this.ticket.id,
      this.data.standingOrders,
    );
  }

  manualDataValid() {
    return !_.some(this.manualDataTypes, (type) => {
      this.setSubmitted(type);
      return this.mainForms[type].$invalid;
    });
  }

  setSubmitted(key) {
    _.forEach(this.inputForms[key], (f) => (f.$submitted = true));
  }

  skip() {
    this.ticketService.transactionDataSkip(this.ticket.id);
  }
}
